import {FC, Fragment, ReactElement, useMemo} from 'react';
import {Typography} from 'components/Typography';
import style from './style.module.scss';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {selectProductData} from 'modules/product-card/selectors';
import {getProductCharacteristics} from 'modules/product-card/helpers/characteristics';
import cs from 'classnames';
import {getProductCatalogPath} from 'modules/product-card/helpers';

interface IProductCharacteristicsValueProps {
    children: string;
}

const ProductCharacteristicsValue: FC<IProductCharacteristicsValueProps> = ({children}) => (
    <Typography className={'product-char-value'} variant={'body-14-reg'}>
        {children}
        <span className={style.valueComma}>, </span>
    </Typography>
);

interface IProductCharacteristicsListProps {
    className?: string;
    moreLink: ReactElement;
}

export const ProductCharacteristicsList: FC<IProductCharacteristicsListProps> = ({className, moreLink}) => {
    const productData = useAppSelector(selectProductData);
    const linkRoot = productData ? `${getProductCatalogPath(productData)}/filter/` : undefined;

    const characteristicsList = useMemo(() => {
        if (!productData) {
            return [];
        }
        return getProductCharacteristics(productData, 'HEADER', undefined, undefined, undefined, linkRoot);
    }, [linkRoot, productData]);

    const detailedCharacteristicsListAvailable = useMemo(() => {
        if (!productData) {
            return [];
        }
        return Boolean(
            getProductCharacteristics(productData, 'DETAILED', undefined, undefined, undefined, linkRoot).length,
        );
    }, [linkRoot, productData]);

    const renderCharacteristicItem = ({
        dataList,
        titleList,
    }: {
        dataList: {link?: string; text: string}[];
        titleList: string[];
    }) => {
        const itemTitle = titleList.join(', ');

        return (
            <Fragment key={itemTitle}>
                <Typography className={cs(style.title, 'char-list-grid-title')} variant={'label-14-m-fluid'}>
                    {itemTitle}:
                </Typography>
                <div className={style.itemDescription} data-skeleton-item={true}>
                    {dataList.map(({link, text: content}: {link?: string; text: string}) => {
                        const valueComponent = <ProductCharacteristicsValue>{content}</ProductCharacteristicsValue>;

                        return link ? (
                            <a className={style.value} href={link} key={content}>
                                {valueComponent}
                            </a>
                        ) : (
                            <span className={cs(style.value, style.noLink, 'char-list-grid-value')} key={content}>
                                {valueComponent}
                            </span>
                        );
                    })}
                </div>
            </Fragment>
        );
    };

    return (
        <div className={className}>
            <div className={cs(style.grid, 'char-list-grid')}>{characteristicsList.map(renderCharacteristicItem)}</div>
            {detailedCharacteristicsListAvailable && moreLink}
        </div>
    );
};
